/**
 * useBuildJobs Hook
 *
 * Hook for managing build jobs state in the planning UI.
 * Connects to BuilderService via window bridge to get real-time updates.
 *
 * NOTE: Window bridge functions are declared in ppt-addin's builderWindowBridge.ts
 * We access them via (window as any) to avoid duplicate declarations.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { IN_PROGRESS_STATUSES, COMPLETED_STATUSES } from "../components/builds/types";
// Configuration constants
const BRIDGE_POLL_INTERVAL_MS = 500; // Interval for checking if bridge is available
const BRIDGE_POLL_TIMEOUT_MS = 10000; // Max time to wait for bridge availability
export const useBuildJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [messages, setMessages] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const unsubscribeRef = useRef(null);
    // Load initial jobs and their messages
    const refreshJobs = useCallback(async () => {
        const win = window;
        if (!win.__getActiveBuildJobs) {
            console.log('[useBuildJobs] Builder service not available yet');
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const activeJobs = await win.__getActiveBuildJobs();
            setJobs(activeJobs);
            // Also load existing messages for each active job
            if (win.__getBuildMessages) {
                const messagePromises = activeJobs.map(async (job) => {
                    try {
                        const jobMessages = await win.__getBuildMessages(job.planId);
                        return { planId: job.planId, messages: jobMessages };
                    }
                    catch (e) {
                        console.warn('[useBuildJobs] Failed to load messages for', job.planId, e);
                        return { planId: job.planId, messages: [] };
                    }
                });
                const allMessages = await Promise.all(messagePromises);
                const messageMap = {};
                for (const { planId, messages: msgs } of allMessages) {
                    if (msgs && msgs.length > 0) {
                        messageMap[planId] = msgs;
                    }
                }
                setMessages(prev => ({ ...prev, ...messageMap }));
            }
            setError(null);
        }
        catch (err) {
            console.error('[useBuildJobs] Error loading jobs:', err);
            setError('Failed to load build jobs');
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    // Subscribe to updates
    useEffect(() => {
        const win = window;
        // Wait for bridge functions to be available
        const checkAndSubscribe = () => {
            if (win.__subscribeToBuildUpdates) {
                console.log('[useBuildJobs] Subscribing to build updates...');
                unsubscribeRef.current = win.__subscribeToBuildUpdates((update) => {
                    console.log('[useBuildJobs] Received update:', update);
                    // Update job state
                    setJobs(prevJobs => {
                        const existingIndex = prevJobs.findIndex(j => j.planId === update.planId);
                        if (existingIndex >= 0) {
                            // Update existing job
                            const updatedJobs = [...prevJobs];
                            updatedJobs[existingIndex] = {
                                ...updatedJobs[existingIndex],
                                // Update planTitle if we have a real one and current is placeholder
                                planTitle: update.planTitle || updatedJobs[existingIndex].planTitle,
                                status: update.status,
                                progress: update.progress,
                                slidesBuilt: update.slidesBuilt,
                                slideCount: update.totalSlides ?? updatedJobs[existingIndex].slideCount,
                                currentSection: update.currentSection,
                                currentActivity: update.currentActivity,
                                sections: update.sections || updatedJobs[existingIndex].sections,
                                errors: update.errors,
                            };
                            // Don't auto-remove completed builds - they stay for the "Completed" tab
                            // Mark newly completed builds as unseen
                            if (COMPLETED_STATUSES.includes(update.status) &&
                                updatedJobs[existingIndex].seen === undefined) {
                                updatedJobs[existingIndex].seen = false;
                            }
                            return updatedJobs;
                        }
                        else if (!['completed', 'failed', 'cancelled'].includes(update.status)) {
                            // Add new job (only if not already finished)
                            return [...prevJobs, {
                                    planId: update.planId,
                                    planTitle: update.planTitle || 'New Build',
                                    status: update.status,
                                    slideCount: update.totalSlides ?? 0,
                                    slidesBuilt: update.slidesBuilt,
                                    progress: update.progress,
                                    currentSection: update.currentSection,
                                    currentActivity: update.currentActivity,
                                    sections: update.sections || [],
                                }];
                        }
                        return prevJobs;
                    });
                    // Add message if present
                    if (update.message) {
                        setMessages(prev => ({
                            ...prev,
                            [update.planId]: [
                                ...(prev[update.planId] || []),
                                update.message,
                            ],
                        }));
                    }
                });
                // Load initial jobs
                refreshJobs();
                return true;
            }
            return false;
        };
        // Try immediately
        if (!checkAndSubscribe()) {
            // Retry with polling
            const interval = window.setInterval(() => {
                if (checkAndSubscribe()) {
                    window.clearInterval(interval);
                }
            }, BRIDGE_POLL_INTERVAL_MS);
            // Clean up interval after timeout
            window.setTimeout(() => window.clearInterval(interval), BRIDGE_POLL_TIMEOUT_MS);
        }
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [refreshJobs]);
    // Actions
    const pauseBuild = useCallback(async (planId) => {
        const win = window;
        if (!win.__pauseBuild) {
            throw new Error('Builder service not available');
        }
        await win.__pauseBuild(planId);
    }, []);
    const resumeBuild = useCallback(async (planId) => {
        const win = window;
        if (!win.__resumeBuild) {
            throw new Error('Builder service not available');
        }
        await win.__resumeBuild(planId);
    }, []);
    const cancelBuild = useCallback(async (planId) => {
        const win = window;
        if (!win.__cancelBuild) {
            throw new Error('Builder service not available');
        }
        await win.__cancelBuild(planId);
        // Immediately remove from local state
        setJobs(prev => prev.filter(j => j.planId !== planId));
    }, []);
    // Note: startBuild is not exposed here because it requires full plan data.
    // Starting builds is done via PlanningContext.startBuildFromPlan() which has
    // access to the complete plan object.
    const removeFromQueue = useCallback(async (planId) => {
        const win = window;
        if (!win.__removeFromQueue) {
            throw new Error('Builder service not available');
        }
        const removed = await win.__removeFromQueue(planId);
        if (removed) {
            // Immediately remove from local state
            setJobs(prev => prev.filter(j => j.planId !== planId));
        }
    }, []);
    const promoteQueuedBuild = useCallback(async (planId) => {
        const win = window;
        if (!win.__promoteQueuedBuild) {
            throw new Error('Builder service not available');
        }
        await win.__promoteQueuedBuild(planId);
        // Refresh jobs to get updated state
        await refreshJobs();
    }, [refreshJobs]);
    const markBuildAsSeen = useCallback((planId) => {
        const win = window;
        // Call the bridge function if available
        if (win.__markBuildAsSeen) {
            win.__markBuildAsSeen(planId);
        }
        // Also update local state immediately for responsiveness
        setJobs(prev => prev.map(job => job.planId === planId ? { ...job, seen: true } : job));
    }, []);
    const getMessages = useCallback((planId) => {
        return messages[planId] || [];
    }, [messages]);
    // Computed: jobs in progress (for "In Progress" section)
    const inProgressJobs = jobs.filter(job => IN_PROGRESS_STATUSES.includes(job.status));
    // Computed: completed/failed/cancelled jobs (for "Completed" tab)
    const completedJobs = jobs.filter(job => COMPLETED_STATUSES.includes(job.status));
    return {
        jobs,
        inProgressJobs,
        completedJobs,
        messages,
        isLoading,
        error,
        pauseBuild,
        resumeBuild,
        cancelBuild,
        removeFromQueue,
        promoteQueuedBuild,
        markBuildAsSeen,
        getMessages,
        refreshJobs,
    };
};
export default useBuildJobs;
